import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  PropsWithChildren,
} from 'react';
import { useApi, storageApiRef, alertApiRef } from '@backstage/core-plugin-api';
import {
  STORAGE_BUCKET,
  FAVORITES_KEY,
  LANDING_PAGE_KEY,
  HIDDEN_ITEMS_KEY,
  MENU_ORDER_KEY,
  PREFERENCES_VERSION_KEY,
  DEFAULT_HIDDEN_ITEMS,
  migrateRetiredSidebarPaths,
  type SidebarPathPreferences,
} from './sidebarConstants';

const STORAGE_PRESENCE = {
  Unknown: 'unknown',
  Present: 'present',
  Absent: 'absent',
} as const;

type StoragePresence =
  (typeof STORAGE_PRESENCE)[keyof typeof STORAGE_PRESENCE];

function logPreferenceLoadError(message: string, error: unknown): void {
  // eslint-disable-next-line no-console
  console.error(message, error);
}

// Every key the retired-path migration needs before it can decide what to do.
const OBSERVED_PREFERENCE_KEYS = [
  PREFERENCES_VERSION_KEY,
  MENU_ORDER_KEY,
  HIDDEN_ITEMS_KEY,
  LANDING_PAGE_KEY,
  FAVORITES_KEY,
];

interface SidebarPreferencesContextValue {
  favorites: string[];
  landingPage: string | undefined;
  hiddenItems: string[];
  isPreferencesReady: boolean;
  landingPageError: string | null;
  hiddenItemsError: string | null;
  toggleFavorite: (path: string) => Promise<void>;
  setLandingPage: (path: string | undefined) => Promise<void>;
  hideItem: (path: string) => Promise<void>;
  unhideItem: (path: string) => Promise<void>;
  unhideAll: () => Promise<void>;
  applySharedNav: (
    nav: {
      hiddenItems: string[];
      menuOrder: string[];
      landingPage?: string;
    },
    previous: { menuOrder: string[] },
  ) => Promise<void>;
}

type PreferenceSnapshot = { presence: StoragePresence; value?: unknown };

function presentSnapshot(value: unknown): PreferenceSnapshot {
  return { presence: STORAGE_PRESENCE.Present, value };
}

function absentSnapshot(): PreferenceSnapshot {
  return { presence: STORAGE_PRESENCE.Absent };
}

const SidebarPreferencesContext = createContext<
  SidebarPreferencesContextValue | undefined
>(undefined);

const SidebarPreferencesProviderInner = ({
  children,
}: PropsWithChildren<{}>) => {
  const baseStorageApi = useApi(storageApiRef);
  const alertApi = useApi(alertApiRef);
  const storageApi = useMemo(
    () => baseStorageApi.forBucket(STORAGE_BUCKET),
    [baseStorageApi],
  );

  const [favorites, setFavorites] = useState<string[]>([]);
  const [landingPage, setLandingPage] = useState<string | undefined>(undefined);
  const [hiddenItems, setHiddenItems] =
    useState<string[]>(DEFAULT_HIDDEN_ITEMS);
  const [isLandingPageReady, setIsLandingPageReady] = useState(false);
  const [isHiddenItemsReady, setIsHiddenItemsReady] = useState(false);
  const [landingPageError, setLandingPageError] = useState<string | null>(null);
  const [hiddenItemsError, setHiddenItemsError] = useState<string | null>(null);
  const initializedRef = useRef(false);
  const isInitializingDefaultsRef = useRef(false);

  // Subscribe to favorites changes
  useEffect(() => {
    const subscription = storageApi
      .observe$<string[]>(FAVORITES_KEY)
      .subscribe({
        next: snapshot => {
          if (snapshot.presence === STORAGE_PRESENCE.Present && snapshot.value) {
            setFavorites(snapshot.value);
          } else if (snapshot.presence === STORAGE_PRESENCE.Absent) {
            setFavorites([]);
          }
        },
      });

    // Get initial value
    const initial = storageApi.snapshot<string[]>(FAVORITES_KEY);
    if (initial.presence === STORAGE_PRESENCE.Present && initial.value) {
      setFavorites(initial.value);
    }

    return () => subscription.unsubscribe();
  }, [storageApi]);

  // Subscribe to landing page changes
  useEffect(() => {
    const subscription = storageApi
      .observe$<string>(LANDING_PAGE_KEY)
      .subscribe({
        next: snapshot => {
          if (snapshot.presence === STORAGE_PRESENCE.Unknown) {
            return;
          }
          if (snapshot.presence === STORAGE_PRESENCE.Present && snapshot.value) {
            setLandingPage(snapshot.value);
          } else if (snapshot.presence === STORAGE_PRESENCE.Absent) {
            setLandingPage(undefined);
          }
          setLandingPageError(null);
          setIsLandingPageReady(true);
        },
        error: error => {
          logPreferenceLoadError(
            'Failed to load landing page preference.',
            error,
          );
          setLandingPageError('Failed to load landing page preference.');
          setIsLandingPageReady(true);
        },
      });

    // Get initial value
    const initial = storageApi.snapshot<string>(LANDING_PAGE_KEY);
    if (initial.presence === STORAGE_PRESENCE.Present && initial.value) {
      setLandingPage(initial.value);
    }
    if (initial.presence !== STORAGE_PRESENCE.Unknown) {
      setLandingPageError(null);
      setIsLandingPageReady(true);
    }

    return () => subscription.unsubscribe();
  }, [storageApi]);

  // Subscribe to hidden items changes
  useEffect(() => {
    const initializeDefaults = async () => {
      if (isInitializingDefaultsRef.current) {
        return;
      }
      isInitializingDefaultsRef.current = true;
      try {
        await storageApi.set(HIDDEN_ITEMS_KEY, DEFAULT_HIDDEN_ITEMS);
        setHiddenItems(DEFAULT_HIDDEN_ITEMS);
        setHiddenItemsError(null);
        initializedRef.current = true;
      } catch (error) {
        setHiddenItems(DEFAULT_HIDDEN_ITEMS);
        setHiddenItemsError('Failed to initialize hidden items.');
      } finally {
        isInitializingDefaultsRef.current = false;
        setIsHiddenItemsReady(true);
      }
    };

    const applyAbsentSnapshot = () => {
      if (!initializedRef.current) {
        // Keep readiness pending until the default write settles. An absent
        // emission during that window is the initial empty snapshot, not a clear.
        initializeDefaults();
        return;
      }
      setHiddenItems([]);
      setHiddenItemsError(null);
      setIsHiddenItemsReady(true);
    };

    const initial = storageApi.snapshot<string[]>(HIDDEN_ITEMS_KEY);
    if (initial.presence === STORAGE_PRESENCE.Present && initial.value) {
      setHiddenItems(initial.value);
      initializedRef.current = true;
      setHiddenItemsError(null);
      setIsHiddenItemsReady(true);
    } else if (initial.presence === STORAGE_PRESENCE.Absent) {
      applyAbsentSnapshot();
    }

    const subscription = storageApi
      .observe$<string[]>(HIDDEN_ITEMS_KEY)
      .subscribe({
        next: snapshot => {
          if (snapshot.presence === STORAGE_PRESENCE.Unknown) {
            return;
          }
          if (snapshot.presence === STORAGE_PRESENCE.Present && snapshot.value) {
            setHiddenItems(snapshot.value);
            initializedRef.current = true;
            setHiddenItemsError(null);
            setIsHiddenItemsReady(true);
            return;
          }
          if (snapshot.presence === STORAGE_PRESENCE.Absent) {
            applyAbsentSnapshot();
          }
        },
        error: error => {
          logPreferenceLoadError(
            'Failed to load hidden items preference.',
            error,
          );
          setHiddenItemsError('Failed to load hidden items preference.');
          setIsHiddenItemsReady(true);
        },
      });

    return () => subscription.unsubscribe();
  }, [storageApi]);

  // Rewrite preferences that still reference the sidebar entries retired by the
  // catalog consolidation.
  useEffect(() => {
    // Read through observe$ rather than snapshot(): this app registers the
    // backend-backed UserSettingsStorage, whose snapshot() always reports
    // `unknown`, so a synchronous read would see no stored preferences at all
    // and the migration would never run.
    const stored: SidebarPathPreferences = {};
    const resolvedKeys = new Set<string>();
    const subscriptions: { unsubscribe: () => void }[] = [];
    let hasMigrated = false;

    const persist = async (
      version: number,
      changes: Omit<SidebarPathPreferences, 'version'>,
    ) => {
      try {
        if (changes.menuOrder) {
          await storageApi.set(MENU_ORDER_KEY, changes.menuOrder);
        }
        if (changes.hiddenItems) {
          await storageApi.set(HIDDEN_ITEMS_KEY, changes.hiddenItems);
        }
        if (changes.landingPage) {
          await storageApi.set(LANDING_PAGE_KEY, changes.landingPage);
        }
        if (changes.favorites) {
          await storageApi.set(FAVORITES_KEY, changes.favorites);
        }
        // Stamped last so a failure part-way through leaves the migration
        // pending and it is retried on the next load.
        await storageApi.set(PREFERENCES_VERSION_KEY, version);
      } catch (error) {
        alertApi.post({
          message: 'Could not update your saved sidebar navigation.',
          severity: 'warning',
          display: 'transient',
        });
      }
    };

    // The hidden-items / favorites subscriptions above propagate rewritten
    // values into state, so the migration only has to write them.
    const migrateOnceResolved = () => {
      if (hasMigrated || resolvedKeys.size < OBSERVED_PREFERENCE_KEYS.length) {
        return;
      }
      hasMigrated = true;
      const { version, ...changes } = migrateRetiredSidebarPaths(stored);
      if (version !== undefined) {
        void persist(version, changes);
      }
    };

    const observe = <T,>(key: string, assign: (value?: T) => void) => {
      subscriptions.push(
        storageApi.observe$<any>(key).subscribe({
          next: snapshot => {
            // `unknown` means the backend has not answered yet; the observable
            // is guaranteed to follow up with a known presence.
            if (snapshot.presence === STORAGE_PRESENCE.Unknown) return;
            assign(snapshot.value as T | undefined);
            resolvedKeys.add(key);
            migrateOnceResolved();
          },
          // Dedicated landing/hidden subscriptions surface load errors to the
          // UI; skip migration for this session if any observed key fails.
          error: () => {},
        }),
      );
    };

    observe<number>(PREFERENCES_VERSION_KEY, value => {
      stored.version = value;
    });
    observe<string[]>(MENU_ORDER_KEY, value => {
      stored.menuOrder = value;
    });
    observe<string[]>(HIDDEN_ITEMS_KEY, value => {
      stored.hiddenItems = value;
    });
    observe<string>(LANDING_PAGE_KEY, value => {
      stored.landingPage = value;
    });
    observe<string[]>(FAVORITES_KEY, value => {
      stored.favorites = value;
    });

    return () => subscriptions.forEach(s => s.unsubscribe());
  }, [storageApi, alertApi]);

  // Mutation functions
  const toggleFavorite = useCallback(
    async (path: string) => {
      try {
        const isFavorite = favorites.includes(path);
        const newFavorites = isFavorite
          ? favorites.filter(f => f !== path)
          : [...favorites, path];
        await storageApi.set(FAVORITES_KEY, newFavorites);
      } catch (error) {
        alertApi.post({
          message: 'Failed to update favorites. Please try again.',
          severity: 'error',
          display: 'transient',
        });
        throw error;
      }
    },
    [favorites, storageApi, alertApi],
  );

  const setLandingPageValue = useCallback(
    async (path: string | undefined) => {
      try {
        if (path === undefined) {
          await storageApi.remove(LANDING_PAGE_KEY);
        } else {
          await storageApi.set(LANDING_PAGE_KEY, path);
        }
      } catch (error) {
        alertApi.post({
          message: 'Failed to update landing page. Please try again.',
          severity: 'error',
          display: 'transient',
        });
        throw error;
      }
    },
    [storageApi, alertApi],
  );

  const hideItem = useCallback(
    async (path: string) => {
      try {
        // Add to hidden items
        const newHiddenItems = [...hiddenItems, path];
        await storageApi.set(HIDDEN_ITEMS_KEY, newHiddenItems);
        // Also remove from favorites if it was favorited
        if (favorites.includes(path)) {
          const newFavorites = favorites.filter(f => f !== path);
          await storageApi.set(FAVORITES_KEY, newFavorites);
        }
      } catch (error) {
        alertApi.post({
          message: 'Failed to hide sidebar item. Please try again.',
          severity: 'error',
          display: 'transient',
        });
        throw error;
      }
    },
    [hiddenItems, favorites, storageApi, alertApi],
  );

  const unhideItem = useCallback(
    async (path: string) => {
      try {
        const newHiddenItems = hiddenItems.filter(item => item !== path);
        await storageApi.set(HIDDEN_ITEMS_KEY, newHiddenItems);
      } catch (error) {
        alertApi.post({
          message: 'Failed to restore sidebar item. Please try again.',
          severity: 'error',
          display: 'transient',
        });
        throw error;
      }
    },
    [hiddenItems, storageApi, alertApi],
  );

  const unhideAll = useCallback(async () => {
    try {
      await storageApi.set(HIDDEN_ITEMS_KEY, []);
    } catch (error) {
      alertApi.post({
        message: 'Failed to restore all items. Please try again.',
        severity: 'error',
        display: 'transient',
      });
      throw error;
    }
  }, [storageApi, alertApi]);

  const applySharedNav = useCallback(
    async (
      nav: {
        hiddenItems: string[];
        menuOrder: string[];
        landingPage?: string;
      },
      previous: { menuOrder: string[] },
    ) => {
      // UserSettingsStorage.snapshot() is always "unknown". Use values already
      // loaded through observe$ instead of blocking apply on snapshot().
      if (!isLandingPageReady || !isHiddenItemsReady) {
        throw new Error(
          'Sidebar preferences are still loading. Please try applying again.',
        );
      }
      // Subscription errors still flip ready so the share card can show them,
      // but in-memory values may be defaults/stale — never use them for rollback.
      if (landingPageError || hiddenItemsError) {
        throw new Error(
          'Sidebar preferences failed to load. Refresh the page and try again.',
        );
      }

      const previousHidden = presentSnapshot(hiddenItems);
      const previousMenuOrder = presentSnapshot(previous.menuOrder);
      const previousLanding = landingPage
        ? presentSnapshot(landingPage)
        : absentSnapshot();
      const previousFavorites = presentSnapshot(favorites);
      const nextFavorites = favorites.filter(
        path => !nav.hiddenItems.includes(path),
      );
      const shouldUpdateFavorites = nextFavorites.length !== favorites.length;
      const completedWrites: Array<{
        key: string;
        previous: PreferenceSnapshot;
      }> = [];

      const restoreSnapshot = async (
        key: string,
        snapshot: PreferenceSnapshot,
      ) => {
        if (
          snapshot.presence === STORAGE_PRESENCE.Present &&
          snapshot.value !== undefined
        ) {
          await storageApi.set(key, snapshot.value as never);
          return;
        }
        if (snapshot.presence === STORAGE_PRESENCE.Absent) {
          await storageApi.remove(key);
        }
      };

      try {
        await storageApi.set(HIDDEN_ITEMS_KEY, nav.hiddenItems);
        completedWrites.push({
          key: HIDDEN_ITEMS_KEY,
          previous: previousHidden,
        });
        await storageApi.set(MENU_ORDER_KEY, nav.menuOrder);
        completedWrites.push({
          key: MENU_ORDER_KEY,
          previous: previousMenuOrder,
        });
        if (nav.landingPage) {
          await storageApi.set(LANDING_PAGE_KEY, nav.landingPage);
          completedWrites.push({
            key: LANDING_PAGE_KEY,
            previous: previousLanding,
          });
        } else {
          await storageApi.remove(LANDING_PAGE_KEY);
          completedWrites.push({
            key: LANDING_PAGE_KEY,
            previous: previousLanding,
          });
        }
        if (shouldUpdateFavorites) {
          await storageApi.set(FAVORITES_KEY, nextFavorites);
          completedWrites.push({
            key: FAVORITES_KEY,
            previous: previousFavorites,
          });
        }
        setHiddenItems(nav.hiddenItems);
        setLandingPage(nav.landingPage);
        setHiddenItemsError(null);
        setLandingPageError(null);
        if (shouldUpdateFavorites) {
          setFavorites(nextFavorites);
        }
      } catch (error) {
        for (const completedWrite of [...completedWrites].reverse()) {
          try {
            await restoreSnapshot(completedWrite.key, completedWrite.previous);
          } catch {
            // Rollback is best-effort; the original apply error is what we surface.
          }
        }
        alertApi.post({
          message: 'Failed to apply shared sidebar. Please try again.',
          severity: 'error',
          display: 'transient',
        });
        throw error;
      }
    },
    [
      alertApi,
      favorites,
      hiddenItems,
      hiddenItemsError,
      isHiddenItemsReady,
      isLandingPageReady,
      landingPage,
      landingPageError,
      storageApi,
    ],
  );

  const isPreferencesReady = isLandingPageReady && isHiddenItemsReady;

  const value: SidebarPreferencesContextValue = useMemo(
    () => ({
      favorites,
      landingPage,
      hiddenItems,
      isPreferencesReady,
      landingPageError,
      hiddenItemsError,
      toggleFavorite,
      setLandingPage: setLandingPageValue,
      hideItem,
      unhideItem,
      unhideAll,
      applySharedNav,
    }),
    [
      favorites,
      landingPage,
      hiddenItems,
      isPreferencesReady,
      landingPageError,
      hiddenItemsError,
      toggleFavorite,
      setLandingPageValue,
      hideItem,
      unhideItem,
      unhideAll,
      applySharedNav,
    ],
  );

  return (
    <SidebarPreferencesContext.Provider value={value}>
      {children}
    </SidebarPreferencesContext.Provider>
  );
};

export const SidebarPreferencesProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  // Reuse an ancestor provider so TopNav / layout / page do not each open
  // separate user-settings observe subscriptions (duplicate multiget).
  const existing = useContext(SidebarPreferencesContext);
  if (existing) {
    return <>{children}</>;
  }

  return (
    <SidebarPreferencesProviderInner>{children}</SidebarPreferencesProviderInner>
  );
};

export const useSidebarPreferences = (): SidebarPreferencesContextValue => {
  const context = useContext(SidebarPreferencesContext);
  if (context === undefined) {
    throw new Error(
      'useSidebarPreferences must be used within a SidebarPreferencesProvider',
    );
  }
  return context;
};
